'use client';
import clsx from 'clsx';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';
import { withStyles } from 'tss-react/mui';

import ChevronDownIcon from '../../icons/ChevronDown';
import ChevronUpIcon from '../../icons/ChevronUp';
import idsTokensDefault from '../../utils/tokens/idsTokens';
import Button from '../Button';
import PopperMenu from '../PopperMenu';
import SelectItem from '../Select/SelectItem';

export const styles = ({ idsTokens = idsTokensDefault }) => ({
  smallSelectItem: {
    height: 32,
    fontSize: idsTokens.typography.font.size.sm,
    lineHeight: idsTokens.typography.lineHeight.sm,
    letterSpacing: idsTokens.typography.spacing.letter.md,
    fontWeight: idsTokens.typography.font.weight.medium,
  },
});

export class SelectButton extends React.Component {
  state = {
    open: false,
    anchorEl: null,
    displayText: '',
    value: this.props.value || this.props.defaultValue,
  };

  componentDidMount = () => {
    // implement defaultValue logic
    const { defaultValue, children } = this.props;
    if (defaultValue?.length) {
      let displayText = '';
      React.Children.forEach(children, (child) => {
        if (defaultValue === child.props.value) {
          displayText = child.props.children;
        }
      });
      this.setState({ displayText });
    }
  };

  componentDidUpdate = () => {
    const { children, open: openProp } = this.props;
    const { open } = this.state;

    if (!React.Children.count(children)) {
      if (open) {
        this.setState({ open: false });
      }
    } else if (openProp !== undefined && openProp !== open) {
      this.setState({ open: openProp });
    }
  };

  handleClick = (e = {}) => {
    const { onClick } = this.props;

    const anchorEl = e.currentTarget;
    this.setState((state) => ({ open: !state.open, anchorEl }));
    onClick && onClick(e);
  };

  handleClose = (e) => {
    const { onClose } = this.props;

    this.setState({ open: false });
    onClose && onClose(e);
  };

  changeValue = (newValue, displayText) => () => {
    const { onChange, noDeselect, value: valueProp, onClose } = this.props;
    const { value } = this.state;

    const selectedValue = valueProp ?? value;
    const endValue = newValue === selectedValue && !noDeselect ? '' : newValue;
    this.setState({
      displayText: newValue === selectedValue && !noDeselect ? '' : displayText,
      open: false,
      value: endValue,
    });
    onChange && onChange(endValue);
    onClose && onClose();
  };

  render() {
    const {
      placeholder,
      children,
      size,
      classes,
      className,
      openClassName,
      selectedClassName,
      noDeselect,
      displayText: displayTextProp,
      value: valueProp,
      widthVariant: widthVariantProp,
      maxItems = 10,
      forwardedRef,
      PopperMenuProps,
      placement,
      onClick,
      onClose,
      ...rest
    } = this.props;
    const { open, anchorEl, displayText, value } = this.state;
    const widthVariant = widthVariantProp ?? (size === 'small' ? 25 : 18);
    const selectedValue = valueProp ?? value;

    return (
      <>
        <Button
          onClick={this.handleClick}
          open={open}
          size={size}
          className={clsx(className, open && openClassName, selectedValue && selectedClassName)}
          iconPosition="end"
          icon={open ? ChevronUpIcon : ChevronDownIcon}
          ref={forwardedRef}
          {...rest}
        >
          {displayTextProp ?? (displayText.length ? displayText : placeholder)}
        </Button>
        <PopperMenu
          anchorEl={anchorEl}
          open={open}
          onClose={this.handleClose}
          placement={placement}
          {...PopperMenuProps}
          PaperProps={{
            ...PopperMenuProps?.PaperProps,
            style: {
              marginLeft: -widthVariant,
              marginTop: 4,
              marginBottom: 4,
              maxHeight: (size === 'small' ? 32 : 40) * maxItems,
              overflowX: 'hidden',
              overflowY: 'auto',
              minWidth: anchorEl ? anchorEl.offsetWidth + widthVariant - 2 : 124,
              ...PopperMenuProps?.PaperProps?.style,
            },
          }}
        >
          {React.Children.map(children, (child) => (
            <SelectItem
              {...child.props}
              size={size}
              disabled={child.props.isTitle || child.props.disabled}
              selected={selectedValue === child.props.value}
              onClick={
                child.props.isTitle
                  ? noop
                  : this.changeValue(child.props.value, child.props.children)
              }
              className={clsx(size === 'small' && classes.smallSelectItem, child.props.className)}
            />
          ))}
        </PopperMenu>
      </>
    );
  }
}

SelectButton.propTypes = {
  /** JSS styles object.
   * @ignore */
  classes: PropTypes.object.isRequired,
  /** The menu items to display in the dropdown. */
  children: PropTypes.node,
  /** The default value. Use when the component is not controlled. */
  defaultValue: PropTypes.any,
  /** If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** The text to display in the button. */
  displayText: PropTypes.string,
  /** Maximum number of menu items shown in the menu. */
  maxItems: PropTypes.number,
  /** If `true`, selecting the previously selected value will not deselect it. */
  noDeselect: PropTypes.bool,
  /** Callback fired when the value changes. */
  onChange: PropTypes.func,
  /** Callback fired when the component is clicked. */
  onClick: PropTypes.func,
  /** Callback fired when the component requests to be closed. */
  onClose: PropTypes.func,
  /** If `true`, the menu is visible. */
  open: PropTypes.bool,
  /** The short hint displayed in the input before the user selects a value. */
  placeholder: PropTypes.string,
  /** Placement of the `Popper`. */
  placement: PropTypes.oneOf([
    'auto-end',
    'auto-start',
    'auto',
    'bottom-end',
    'bottom-start',
    'bottom',
    'left-end',
    'left-start',
    'left',
    'right-end',
    'right-start',
    'right',
    'top-end',
    'top-start',
    'top',
  ]),
  /** Props applied to the `PopperMenu` component. */
  PopperMenuProps: PropTypes.object,
  /** The size of the component. */
  size: PropTypes.oneOf(['small', 'medium']),
  /** The value of the component. */
  value: PropTypes.any,
};

export default withStyles(SelectButton, styles);
